'use client'

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { motion, AnimatePresence } from 'motion/react'
import { ShoppingBag, Menu, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ThemeSwitcher } from '@/components/ui/theme-switcher'
import { usePathname } from 'next/navigation'
import { useAuth } from '@/lib/auth/AuthContext'
import { useLanguage } from '@/components/i18n/LanguageProvider'
import fr from '@/i18n/fr.json'
import en from '@/i18n/en.json'
import { LanguageToggle } from '@/components/ui/language-toggle'
import { CurrencyToggle } from '@/components/ui/currency-toggle'

type ModernHeaderProps = {
  cartCount: number
  onCartClick: () => void
  hasTestimonials?: boolean
}

const navLinks = {
  fr: [
    { key: 'home', hash: 'home' },
    { key: 'story', hash: 'story' },
    { key: 'testimonials', hash: 'testimonials' },
    { key: 'shop', path: '/boutique' },
  ],
  en: [
    { key: 'home', hash: 'home' },
    { key: 'story', hash: 'story' },
    { key: 'testimonials', hash: 'testimonials' },
    { key: 'shop', path: '/boutique' },
  ],
}

export function ModernHeader({ cartCount, onCartClick, hasTestimonials = false }: ModernHeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const { user, loading } = useAuth()
  const pathname = usePathname()
  const { language } = useLanguage()
  const copy = language === 'fr' ? fr : en
  const isHome = useMemo(() => pathname === '/' || pathname === '' || pathname?.startsWith('/#'), [pathname])
  const isAuthenticated = Boolean(user)
  const visibleLinks = useMemo(
    () => navLinks[language].filter((link) => hasTestimonials || link.key !== 'testimonials'),
    [hasTestimonials, language]
  )

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const logoHref = '/#home'

  const handleLogoClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isHome) return
    event.preventDefault()
    const el = document.getElementById('home')
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <>
      <motion.header
        className={`fixed left-0 right-0 top-0 z-50 transition-all duration-300 ${isScrolled ? 'border-b border-border bg-background/80 backdrop-blur-xl shadow-sm' : 'bg-transparent'}`}
        initial={false}
        animate={{ y: 0 }}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link href={logoHref} onClick={handleLogoClick} className="flex items-center gap-3" aria-label="Retour à l’accueil Salamdor">
            <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full bg-primary/90 ring-1 ring-border">
              <img src="/salamdor.png" alt="Salamdor" className="h-full w-full object-contain" />
            </div>
            <span className="text-xl tracking-tight text-foreground">Salamdor</span>
          </Link>

          <nav className="hidden items-center space-x-1 md:flex">
            {visibleLinks.map((link) => {
              const href = link.hash ? (isHome ? `#${link.hash}` : `/#${link.hash}`) : link.path || '/'
              return (
                <Link key={link.key} href={href} className="group relative px-4 py-2 text-foreground/70 transition-colors hover:text-foreground">
                  {copy.nav[link.key as keyof typeof copy.nav]}
                  <span className="absolute bottom-0 left-4 right-4 h-0.5 origin-left scale-x-0 bg-primary transition-transform group-hover:scale-x-100" />
                </Link>
              )
            })}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden items-center gap-2 md:flex">
              <CurrencyToggle />
              <LanguageToggle />
              <ThemeSwitcher />
            </div>
            <div className="hidden items-center gap-2 md:flex">
              {loading && <div className="h-10 w-28 animate-pulse rounded-full bg-muted" aria-label="Chargement du menu" />}
              {!loading && !isAuthenticated && (
                <>
                  <Button asChild variant="ghost" className="rounded-full px-5">
                    <Link href="/login">{copy.nav.login}</Link>
                  </Button>
                  <Button asChild className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                    <Link href="/signup">{copy.nav.signup}</Link>
                  </Button>
                </>
              )}
              {!loading && isAuthenticated && (
                <Button asChild className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                  <Link href={user?.role === 'OWNER' || user?.role === 'SUPER_ADMIN' ? '/dashboard' : '/account/orders'}>
                    {user?.role === 'OWNER' || user?.role === 'SUPER_ADMIN' ? copy.nav.dashboard : copy.nav.account}
                  </Link>
                </Button>
              )}
            </div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button variant="ghost" size="icon" className="relative" onClick={onCartClick}>
                <ShoppingBag className="h-5 w-5" />
                <AnimatePresence>
                  {cartCount > 0 && (
                    <motion.span
                      className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-secondary text-xs text-secondary-foreground"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                    >
                      {cartCount}
                    </motion.span>
                  )}
                </AnimatePresence>
              </Button>
            </motion.div>

            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              aria-expanded={isMobileMenuOpen}
              aria-label="Ouvrir ou fermer le menu principal"
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            >
              {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
              </div>
            </div>
      </motion.header>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="fixed inset-x-0 top-20 z-40 bg-background p-6 md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <nav className="flex flex-col gap-4">
              {visibleLinks.map((link, index) => {
                const href = link.hash ? (isHome ? `#${link.hash}` : `/#${link.hash}`) : link.path || '/'
                return (
                  <motion.div
                    key={link.key}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link href={href} className="block text-2xl text-foreground transition-colors hover:text-primary" onClick={() => setIsMobileMenuOpen(false)}>
                      {copy.nav[link.key as keyof typeof copy.nav]}
                    </Link>
                  </motion.div>
                )
              })}
              <div className="mt-4 flex flex-col gap-3">
                <CurrencyToggle />
                <LanguageToggle />
                <ThemeSwitcher compact />
                {loading && <div className="h-10 w-full animate-pulse rounded-full bg-muted" aria-label="Chargement du menu" />}
                {!loading && !isAuthenticated && (
                  <>
                    <Button asChild variant="ghost" className="rounded-full">
                      <Link href="/login" onClick={() => setIsMobileMenuOpen(false)}>
                        {copy.nav.login}
                      </Link>
                    </Button>
                    <Button asChild className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                      <Link href="/signup" onClick={() => setIsMobileMenuOpen(false)}>
                        {copy.nav.signup}
                      </Link>
                    </Button>
                  </>
                )}
                {!loading && isAuthenticated && (
                  <Button asChild className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                    <Link href={user?.role === 'OWNER' || user?.role === 'SUPER_ADMIN' ? '/dashboard' : '/account/orders'} onClick={() => setIsMobileMenuOpen(false)}>
                      {user?.role === 'OWNER' || user?.role === 'SUPER_ADMIN' ? copy.nav.dashboard : copy.nav.account}
                    </Link>
                  </Button>
                )}
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
